import React from 'react';
import { Label } from './ui/label';
import { OCCUPATION_CONTEXTS, EVENT_CONTEXTS } from '../lib/clientContexts';
import {
  Sparkles, Heart, Users,
  Scale, TrendingUp, Cpu, Briefcase, Camera, Stethoscope, Mic, Home, Landmark,
  PartyPopper, Gem, Wine, Sun, Plane, Search,
} from 'lucide-react';

// Iter 231 (Feb 2026) — shared occupation / event tile picker used by
// both the intake Step-2 flow and the sidebar Wardrobe-Context "Change"
// popout. Kept as a single stateless component so the two entry points
// stay in sync.
const ICONS = {
  Scale, TrendingUp, Cpu, Briefcase, Camera, Stethoscope, Mic, Home, Landmark,
  Heart, Users, PartyPopper, Gem, Wine, Sun, Plane, Search,
};

export function ContextTilePicker({
  value,
  onChange,
  accent = '#C2A878',
  activeColor,        // fill for the picked tile — defaults to `accent`
  testidPrefix = 'context-tile',
}) {
  const active = activeColor || accent;
  const Tile = ({ opt }) => {
    const Icon = ICONS[opt.icon] || Sparkles;
    const on = value === opt.value;
    return (
      <button
        key={opt.value}
        type="button"
        onClick={() => onChange(opt.value)}
        className="group relative flex flex-col items-center justify-center text-center gap-2 border transition-colors py-4 px-2 min-h-[92px]"
        style={{
          backgroundColor: on ? active : '#0A0A0C',
          color: on ? '#0A0A0C' : '#F5F5F5',
          borderColor: on ? active : '#27272A',
        }}
        data-testid={`${testidPrefix}-${opt.value}`}
      >
        <Icon
          className="w-6 h-6 transition-transform group-hover:scale-110"
          style={{ color: on ? '#0A0A0C' : accent }}
          strokeWidth={1.5}
        />
        <span className="text-[10px] uppercase tracking-widest font-semibold leading-tight">
          {opt.label}
        </span>
      </button>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-3">
          <Label className="label-luxury">Occupation</Label>
          <span className="text-[9px] uppercase tracking-widest text-[#71717A]">
            Their day-to-day work
          </span>
        </div>
        <div
          className="grid grid-cols-2 sm:grid-cols-3 gap-2"
          data-testid={`${testidPrefix}-occupation-grid`}
        >
          {OCCUPATION_CONTEXTS.map(opt => <Tile key={opt.value} opt={opt} />)}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <Label className="label-luxury">Event / Moment</Label>
          <span className="text-[9px] uppercase tracking-widest text-[#71717A]">
            An upcoming occasion
          </span>
        </div>
        <div
          className="grid grid-cols-2 sm:grid-cols-3 gap-2"
          data-testid={`${testidPrefix}-event-grid`}
        >
          {EVENT_CONTEXTS.map(opt => <Tile key={opt.value} opt={opt} />)}
        </div>
      </div>
    </div>
  );
}

/**
 * Return the lucide-react icon component for a given context value, or
 * `Sparkles` when the value is unknown / null. Used by the compact
 * "current selection" tile in the sidebar.
 */
export function iconForContext(value) {
  const opt = [...OCCUPATION_CONTEXTS, ...EVENT_CONTEXTS].find(c => c.value === value);
  if (!opt) return Sparkles;
  return ICONS[opt.icon] || Sparkles;
}

export default ContextTilePicker;
